import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, RefreshCw } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminShell } from '../components/AdminShell';
import { api, errMsg } from '../lib/api';
import { peso, manilaDate, PLAN_LABELS } from '../lib/format';

const STATUS_COLORS = {
  trialing: 'bg-sky-100 text-sky-700', active: 'bg-emerald-100 text-emerald-700',
  past_due: 'bg-amber-100 text-amber-800', suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [busy, setBusy] = useState(false);

  const load = async (query = q, st = status) => {
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (st !== 'all') params.status = st;
      const [{ data: s }, { data: m }] = await Promise.all([
        api.get('/admin/shops', { params }),
        api.get('/admin/metrics'),
      ]);
      setShops(s.shops);
      setMetrics(m);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const runLifecycle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/run-lifecycle');
      toast.success(data.message);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold font-display">Shops</h1>
        <Button data-testid="admin-run-lifecycle-button" variant="outline" disabled={busy} className="h-10 rounded-xl" onClick={runLifecycle}>
          <RefreshCw className="h-4 w-4 mr-1.5" /> Run daily lifecycle job
        </Button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="rounded-2xl border-black/5"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-[hsl(var(--muted-foreground))]">MRR (active shops)</p>
            <p data-testid="admin-mrr" className="text-xl font-semibold font-display tabular-nums">{peso(metrics.mrr_centavos)}</p>
          </CardContent></Card>
          <Card className="rounded-2xl border-black/5"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Collected this month</p>
            <p data-testid="admin-collected" className="text-xl font-semibold font-display tabular-nums">{peso(metrics.collected_this_month_centavos)}</p>
          </CardContent></Card>
          <Card className="rounded-2xl border-black/5 col-span-2"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Shops by status</p>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(metrics.status_counts).map(([k, v]) => (
                <span key={k} className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[k]}`}>{k.replace('_', ' ')}: {v}</span>
              ))}
            </div>
          </CardContent></Card>
        </div>
      )}

      <div className="flex gap-2 flex-col sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <Input
            data-testid="admin-shops-filter-input"
            placeholder="Search shop name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="h-11 rounded-xl pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-status-filter-select" className="h-11 rounded-xl sm:w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['trialing', 'active', 'past_due', 'suspended', 'cancelled'].map((s) => (
              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {shops === null ? (
        <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : shops.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-8">No shops match your filters.</p>
      ) : (
        <div className="space-y-2">
          {shops.map((s) => (
            <button
              key={s.id}
              data-testid="admin-shop-row"
              onClick={() => navigate(`/admin/shops/${s.id}`)}
              className="w-full text-left rounded-2xl border border-black/5 bg-white p-4 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-card)] transition-shadow"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium">{s.name}</span>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-semibold">{PLAN_LABELS[s.plan]}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[s.subscription_status]}`}>{s.subscription_status.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))] flex gap-3 flex-wrap">
                <span>{s.user_count} user{s.user_count === 1 ? '' : 's'}</span>
                <span>{s.open_job_count} open job{s.open_job_count === 1 ? '' : 's'}</span>
                {s.subscription_status === 'trialing' && s.trial_ends_at && <span>Trial ends {manilaDate(s.trial_ends_at)}</span>}
                {s.paid_until && <span>Paid until {manilaDate(s.paid_until)}</span>}
                <span>Since {manilaDate(s.created_at)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
